import { Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { finalize, take } from 'rxjs/operators';
import { RegionService } from '../../../core/api/region.service';
import { SpinnerService } from '../../../shared/spinner/spinner.service';
import { selectAdminFiltersAndPage } from '../../../store/admin/admin.selectors';
import * as fromApp from '../../../store/index';
import { AdminTableBase } from '../admin-table-base/admin-table-base';
import { CountyInfo, GisService } from "../../../core/api/gis.service";
import { DialogYesNoComponent } from "../../../shared/dialogs/dialog-yes-no/dialog-yes-no.component";
import { MatDialog } from "@angular/material/dialog";
import { DialogEditCountyDataComponent } from "../../../shared/dialogs/dialog-edit-county-data/dialog-edit-county-data.component";
import { setDashboardFilter } from "../../../store/admin/admin.actions";
import { MatSelectChange } from "@angular/material/select";
import { UtilitiesService } from "../../../core/api/utlities.service";

@Component({
  selector: 'fc-manage-counties-page',
  templateUrl: './manage-counties-page.component.html',
  styleUrls: ['./manage-counties-page.component.scss'],
})
export class ManageCountiesPageComponent extends AdminTableBase implements OnInit {
  destroyRef = inject(DestroyRef);
  dataSource: MatTableDataSource<CountyInfo> = new MatTableDataSource<CountyInfo>();
  @ViewChild('filterCounty', { static: false }) filterCounty: any;
  @ViewChild('filterState', { static: false }) filterState: any;

  readonly PAGE_NAME: string = 'counties';
  displayedColumns: string[] = ['available', 'outDate', 'county', 'state', 'regridFile', 'regridRefresh', 'lastImport', 'status', 'averageErt', 'actions'];

  filterValues = {
    county: '',
    state: '',
    available: true,
    up_to_date: null
  };

  defaultSortQuery = '&sort=state(asc)';
  sortQuery = this.defaultSortQuery;
  upToDateQuery = '';

  sortBy = {
    state: this.sortStates.ascending,
    county: this.sortStates.ascending,
  };

  constructor(
    readonly dialog: MatDialog,
    readonly router: Router,
    readonly store: Store<fromApp.AppState>,
    readonly gisService: GisService,
    readonly spinnerService: SpinnerService,
    readonly regionService: RegionService,
    readonly utilitiesService: UtilitiesService,
  ) {
    super();
  }

  ngOnInit(): void {
    this.store
      .select(selectAdminFiltersAndPage)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(state => {
        super.setUpAdminFilter(state.filters);
        this.initializeFilters();
        this.doApplyFilterSubject({ value: true }, 'available', 'eq');
      });
  }

  getBaseQueryString(): string {
    return `?count=${this.paginationTotal}&offset=${this.offset}`;
  }

  clearFilters(): void {
    if (this.getBaseQueryString() !== this.getFullQueryString()) {
      super.clearFilters();
      this.upToDateQuery = '';
      this.filterCounty.nativeElement.value = '';
      this.filterState.nativeElement.value = '';
      this.toggleSingleSort('state');
    }
  }

  editCounty(row) {
    this.dialog.open(DialogEditCountyDataComponent, {
      panelClass: 'fc-edit-county-data-dialog',
      data: {
        countyData: row,
      },
    }).afterClosed().subscribe(() => {
      this.queryItems();
    })
  }

  queryItems() {
    this.spinnerService.show('county-spinner', true, 'Getting county data...');
    this.gisService
      .getFilteredCountyData(this.getFullQueryString())
      .pipe(finalize(() => this.spinnerService.hide('county-spinner')))
      .subscribe(programResults => {
        this.dataSource = new MatTableDataSource(programResults.results);
        this.totalItems = programResults.total;
        this.setAllPages();
        this.setStartEndIndexes();
      });
  }

  updateRegridDates() {
    this.dialog.open(DialogYesNoComponent, {
      panelClass: 'fc-yes-no-dialog',
      data: {
        title: 'Update Regrid dates',
        line1: `This will update all regrid counties with the last refresh date. This may take several seconds. Please confirm.`,
        buttonTextYes: 'Confirm',
        buttonTextNo: 'Cancel',
      },
    }).afterClosed().subscribe(result => {
      if (result) {
        this.spinnerService.show('county-spinner', true, 'Running Regrid update...');
        this.gisService
          .updateRegridDates()
          .subscribe(
            response => {
              this.queryItems();
            },
            () => {
              this.spinnerService.hide('county-spinner');
            }
          );
      }
    });
  }

  updateCounty(row) {
    this.dialog.open(DialogYesNoComponent, {
      panelClass: 'fc-yes-no-dialog',
      data: {
        title: 'County Update',
        line1: `This will queue county data for download from Regrid. To begin the caching, an IT Admin will need to initiate.`,
        buttonTextYes: 'Confirm',
        buttonTextNo: 'Cancel',
      },
    }).afterClosed().subscribe(result => {
      if (result) {
        this.spinnerService.show('county-spinner', true, 'Starting Regrid update...').then(() => {
          this.gisService
            .queueRegridDownload(row.gid)
            .subscribe(
              (response) => {
                this.queryItems();
              },
              () => {
                this.spinnerService.hide('county-spinner');
              }
            );
        })
      }
    });
  }

  beginCache() {
    this.dialog.open(DialogYesNoComponent, {
      panelClass: 'fc-yes-no-dialog',
      data: {
        title: 'County Cache',
        line1: 'This will begin the cache process with a max of 5 counties at a time. The full process will take several minutes. To track the updates, refresh the list.',
        buttonTextYes: 'Confirm',
        buttonTextNo: 'Cancel',
      },
    }).afterClosed().subscribe(result => {
      if (result) {
        this.spinnerService.show('county-spinner', true, 'Starting Regrid caching...').then(() => {
          this.gisService
            .beginCaching()
            .subscribe(
              (response) => {
                this.queryItems();
              },
              () => {
                this.spinnerService.hide('county-spinner');
              }
            );
        })
      }
    });
  }

  updateUpToDateQuery($event: MatSelectChange, field: string) {
    if ($event.value == null) {
      this.upToDateQuery = '';
    } else {
      this.upToDateQuery = `&${field}=${$event.value}`;
    }

    this.store.dispatch(
      setDashboardFilter({
        filter: 'upToDateQuery',
        value: this.upToDateQuery,
        page: this.PAGE_NAME,
      })
    );

    this.resetOffsetAndCurrentPage();
    this.queryItems();
  }
}
